import { BaseText, theme } from 'aimo-ui';
import MenuItem from 'components/common/MenuItem';
import QuickActionDirection from 'components/common/QuickActionDirection';
import Icon from 'components/icons/Icon';
import InfoIcon from 'components/icons/InfoIcon';
import FeatureFlagsContext from 'context/FeatureFlagsContext';
import { format, isAfter } from 'date-fns';
import useSelectedPaymentMethod, {
  SelectedPaymentMethod,
} from 'hooks/useSelectedPaymentMethod';
import { t } from 'i18next';
import React, { useContext } from 'react';
import PermitParkingSpace from 'screens/permit/PermitParkingSpace.tsx';
import { usePermitStore } from 'stores/permitStore';
import { useUiStore } from 'stores/uiStore';
import { CenterVerticalRow, StyledWrapper } from 'styles/ContainerStyles';
import {
  BillingType,
  LicensePlate,
  Maybe,
  ParkingSpace,
  ReservationType,
} from 'types/generatedSchemaTypes.ts';
import { isSalesOrderFree } from 'utils/commonUtils';
import { showFeatureFlag } from 'utils/featureFlagUtils';
import { makePoint } from 'utils/mapUtils';
import { getLocationIconType, locationIcon } from 'utils/parkUtils';
import { getWalletCardMenuPresentation } from 'utils/paymentUtils';
import {
  getLicensePlatesText,
  shouldShowSalesOrderDoorCode,
} from 'utils/permitUtils.ts';
import { SalesOrderReceipt } from '../SalesOrderReceiptClass';

type PermitInfoRow = {
  icon?: string;
  label: string;
  value?: React.ReactNode;
  onPress?: () => void;
  isPaymentCardRow?: boolean;
};

type PermitInfoRowParams = {
  isSubscription: boolean;
  isHistory: boolean;
  salesOrder: SalesOrderReceipt;
  title?: Maybe<string>;
  zoneName?: Maybe<string>;
  doorCode?: Maybe<string>;
  reservationType?: Maybe<ReservationType>;
  parkingSpaces?: Maybe<Array<Maybe<ParkingSpace>>>;
  spotMap?: Maybe<string>;
  parkZoneName?: Maybe<string>;
  markerCoordinate?: { latitude: number; longitude: number } | null;
  markerType: string;
  plateText: string;
  registrationPlates: LicensePlate[];
  usageType?: Maybe<string>;
  orderLineIdWithVersion?: Maybe<string>;
  validFromFormatted?: string;
  billingType?: Maybe<BillingType>;
  isInvoicing?: boolean;
  isWaiting?: boolean;
  isEnding?: boolean;
  recurring?: boolean;
  endDate?: Maybe<Date | string>;
  renewalTime?: Maybe<Date | string>;
  startDate: string;
  endDateFormatted: string;
  paymentCard?: SelectedPaymentMethod;
  creditCardId?: Maybe<string>;
  freeSalesOrder: boolean;
};

const openPermitSheet = (
  orderLineIdWithVersion: Maybe<string> | undefined,
  sheet: 'parkingSpaces' | 'registrationPlates'
) => {
  usePermitStore
    .getState()
    .setSelectedPermit(orderLineIdWithVersion ?? undefined);
  useUiStore.getState().setSheetModal(sheet);
};

const getLocationRow = ({
  isSubscription,
  markerType,
  zoneName,
  title,
  parkZoneName,
  markerCoordinate,
}: Pick<
  PermitInfoRowParams,
  | 'isSubscription'
  | 'markerType'
  | 'zoneName'
  | 'title'
  | 'parkZoneName'
  | 'markerCoordinate'
>): PermitInfoRow | undefined => {
  if (isSubscription) {
    return undefined;
  }
  return {
    icon: locationIcon[markerType],
    label: zoneName || title || '',
    value: (
      <QuickActionDirection
        name={parkZoneName}
        showIconLabel={false}
        markerCoordinate={markerCoordinate}
        alignEnd
      />
    ),
  };
};

const getSubscriptionBillingRow = ({
  isSubscription,
  startDate,
  endDateFormatted,
}: Pick<
  PermitInfoRowParams,
  'isSubscription' | 'startDate' | 'endDateFormatted'
>): PermitInfoRow | undefined => {
  if (!isSubscription) {
    return undefined;
  }
  return {
    icon: 'Calendar-Outlined',
    label: t('account.aimoSubscription.receiptBillingPeriod'),
    value: `${startDate} - ${endDateFormatted}`,
  };
};

const getDoorCodeRow = ({
  doorCode,
  isHistory,
  salesOrder,
}: Pick<PermitInfoRowParams, 'doorCode' | 'isHistory' | 'salesOrder'>):
  | PermitInfoRow
  | undefined => {
  if (
    !shouldShowSalesOrderDoorCode({
      doorCode,
      isHistory,
      salesOrder,
    })
  ) {
    return undefined;
  }
  return {
    icon: 'Unlocked-Outlined',
    label: t('longTermParking.garageEntryCode'),
    value: doorCode,
  };
};

const getParkingSpaceRow = ({
  isHistory,
  reservationType,
  parkingSpaces,
  spotMap,
  orderLineIdWithVersion,
}: Pick<
  PermitInfoRowParams,
  | 'isHistory'
  | 'reservationType'
  | 'parkingSpaces'
  | 'spotMap'
  | 'orderLineIdWithVersion'
>): PermitInfoRow | undefined => {
  if (isHistory) {
    return undefined;
  }
  const isReserved = reservationType === 'RESERVED';
  return {
    icon: 'Parking_Spot-Outlined',
    label: t('longTermParking.parkingSpace'),
    value: isReserved ? (
      <PermitParkingSpace
        parkingSpaces={parkingSpaces as ParkingSpace[]}
        spotMap={spotMap}
        textColor={theme.colors.gray300}
      />
    ) : (
      t('longTermParking.any')
    ),
    onPress: isReserved
      ? () => openPermitSheet(orderLineIdWithVersion, 'parkingSpaces')
      : undefined,
  };
};

const getVehicleRow = ({
  plateText,
  usageType,
  isSubscription,
  reservationType,
  registrationPlates,
  orderLineIdWithVersion,
}: Pick<
  PermitInfoRowParams,
  | 'plateText'
  | 'usageType'
  | 'isSubscription'
  | 'reservationType'
  | 'registrationPlates'
  | 'orderLineIdWithVersion'
>): PermitInfoRow | undefined => {
  const isCustomerPermit = usageType === 'CUSTOMER';
  if (!plateText || usageType === 'SKIDATA' || isSubscription) {
    return undefined;
  }
  const isReserved = reservationType === 'RESERVED';
  const hasNoPlates = registrationPlates.length === 0;
  const vehicleText =
    isCustomerPermit && hasNoPlates ? t('longTermParking.any') : plateText;
  return {
    icon: 'Car-Outlined',
    label: t('services.permitVehicle'),
    value: (
      <CenterVerticalRow>
        <BaseText
          color={
            hasNoPlates && !isCustomerPermit
              ? theme.colors.aimoUiRed
              : theme.colors.gray300
          }>
          {vehicleText}
        </BaseText>
        <StyledWrapper mb={-2}>
          {isReserved && registrationPlates.length > 2 ? (
            <Icon name="Chevron_Right" size="s" />
          ) : null}
        </StyledWrapper>
      </CenterVerticalRow>
    ),
    onPress:
      isReserved && !isCustomerPermit
        ? () => openPermitSheet(orderLineIdWithVersion, 'registrationPlates')
        : undefined,
  };
};

const getValidFromRow = ({
  validFromFormatted,
}: Pick<PermitInfoRowParams, 'validFromFormatted'>):
  | PermitInfoRow
  | undefined => {
  if (!validFromFormatted) {
    return undefined;
  }
  return {
    icon: 'Calendar-Outlined',
    label: t('permit.access.validFrom'),
    value: validFromFormatted,
  };
};

const getBillingDateRow = ({
  billingType,
  isInvoicing,
  isWaiting,
  endDate,
  renewalTime,
  recurring,
  isEnding,
  endDateFormatted,
}: Pick<
  PermitInfoRowParams,
  | 'billingType'
  | 'isInvoicing'
  | 'isWaiting'
  | 'endDate'
  | 'renewalTime'
  | 'recurring'
  | 'isEnding'
  | 'endDateFormatted'
>): PermitInfoRow | undefined => {
  const showBillingDate =
    billingType !== 'ONE_TIME' &&
    ((!isInvoicing && !isWaiting) || (isInvoicing && endDate));
  if (!showBillingDate) {
    return undefined;
  }
  return {
    icon: renewalTime !== undefined ? 'Calendar-Outlined' : undefined,
    label:
      recurring && !isEnding
        ? t('services.nextBillingDate')
        : t('permit.expires'),
    value: endDateFormatted,
  };
};

export const getPaymentCardRow = ({
  paymentCard,
  isInvoicing,
  freeSalesOrder,
}: Pick<
  PermitInfoRowParams,
  'paymentCard' | 'isInvoicing' | 'freeSalesOrder'
>): PermitInfoRow | undefined => {
  if (!paymentCard?.lastNums || isInvoicing || freeSalesOrder) {
    return undefined;
  }
  const walletCard = getWalletCardMenuPresentation(paymentCard, t);
  return {
    icon: walletCard?.iconName ?? 'Credit_Card-Outlined',
    label: walletCard ? t('services.paymentMethod') : t('payment.creditCard'),
    value: walletCard?.subtitle ?? `**** ${paymentCard.lastNums}`,
    isPaymentCardRow: true,
  };
};

const getInvoicingRow = ({
  isInvoicing,
}: Pick<PermitInfoRowParams, 'isInvoicing'>): PermitInfoRow | undefined => {
  if (!isInvoicing) {
    return undefined;
  }
  return {
    icon: 'Document-Outlined',
    label: t('paymentMethods.invoicing'),
  };
};

export const getNextCardRow = ({
  paymentCard,
  creditCardId,
  isInvoicing,
  isEnding,
  freeSalesOrder,
  billingType,
}: Pick<
  PermitInfoRowParams,
  | 'paymentCard'
  | 'creditCardId'
  | 'isInvoicing'
  | 'isEnding'
  | 'freeSalesOrder'
  | 'billingType'
>): PermitInfoRow | undefined => {
  if (
    paymentCard?.cardId === creditCardId ||
    !paymentCard?.lastNums ||
    isInvoicing ||
    isEnding ||
    freeSalesOrder ||
    billingType === 'ONE_TIME'
  ) {
    return undefined;
  }
  const walletCard = getWalletCardMenuPresentation(paymentCard, t);
  return {
    icon: walletCard?.iconName ?? 'Credit_Card-Outlined',
    label: t('payment.nextCreditCard'),
    value: walletCard?.subtitle ?? `**** ${paymentCard.lastNums}`,
    isPaymentCardRow: true,
  };
};

const buildPermitInfoList = (params: PermitInfoRowParams): PermitInfoRow[] =>
  [
    getLocationRow(params),
    getSubscriptionBillingRow(params),
    getDoorCodeRow(params),
    getParkingSpaceRow(params),
    getVehicleRow(params),
    getValidFromRow(params),
    getBillingDateRow(params),
    getPaymentCardRow(params),
    getInvoicingRow(params),
    getNextCardRow(params),
  ].filter((row): row is PermitInfoRow => row !== undefined);

const SalesOrderPermitInfo = ({
  salesOrderReceipt,
  isHistory,
  isSubscription = false,
}: {
  salesOrderReceipt: SalesOrderReceipt;
  isHistory: boolean;
  isSubscription?: boolean;
}) => {
  const {
    orderLines,
    creditCardId,
    title,
    isInvoicing,
    nextBillingDate: renewalTime,
    isRecurring: recurring,
    isWaiting,
    endDate,
    created,
    isEnding,
    billingType,
    orderLineIdWithVersion,
    isFailedRecurringPaymentForSalesOrder,
    usageType,
  } = salesOrderReceipt || {};
  const orderLine = orderLines?.[0];
  const validFrom = orderLine?.validity?.validFrom;
  const validFromFormatted = isAfter(
    new Date(validFrom ?? new Date()),
    new Date()
  )
    ? format(new Date(validFrom ?? new Date()), 'd.M.y')
    : undefined;

  const { pmc, parkingSpaces } = orderLine || {};
  const registrationPlates = (orderLines?.[0]?.registrationPlates ?? []).filter(
    (lp): lp is LicensePlate => lp !== null
  );
  const plateText = getLicensePlatesText(registrationPlates);
  const { address, name, commercialZoneView, spotMap } = pmc?.parkZone || {};
  const { prices } = pmc || {};
  const markerCoordinate =
    address?.marker && makePoint(address?.marker?.coordinates);
  const parkZoneName = name || commercialZoneView?.name;
  const {
    flags: { anpr },
  } = useContext(FeatureFlagsContext);
  const showCameraRecognition = showFeatureFlag(anpr);
  const paymentCard = useSelectedPaymentMethod(creditCardId || undefined);
  const markerType = getLocationIconType(
    pmc?.parkZone?.locationType || 'OTHER',
    showCameraRecognition && pmc?.parkZone?.parkingMethods?.anpr
  );
  const freeSalesOrder = isSalesOrderFree(prices);

  const endDateFormatted = format(
    new Date(endDate || renewalTime || new Date()),
    'd.M.y'
  );
  const startDate = format(new Date(created || new Date()), 'd.M.y');
  const permitInfoList = buildPermitInfoList({
    isSubscription,
    isHistory,
    salesOrder: salesOrderReceipt,
    title,
    zoneName: pmc?.parkZone?.name,
    doorCode: pmc?.parkZone?.info?.doorCode,
    reservationType: pmc?.reservationType,
    parkingSpaces,
    spotMap,
    parkZoneName,
    markerCoordinate,
    markerType,
    plateText,
    registrationPlates,
    usageType,
    orderLineIdWithVersion,
    validFromFormatted,
    billingType,
    isInvoicing,
    isWaiting,
    isEnding,
    recurring,
    endDate,
    renewalTime,
    startDate,
    endDateFormatted,
    paymentCard,
    creditCardId,
    freeSalesOrder,
  });

  return (
    <>
      {permitInfoList.map((item, index) =>
        item?.icon ? (
          <MenuItem
            testID={`permit-info-${index}`}
            iconName={item.icon}
            menuLabel={item.label}
            rightComponent={
              <BaseText color={theme.colors.gray300}>
                {item.value}
                {item.isPaymentCardRow &&
                  isFailedRecurringPaymentForSalesOrder && <InfoIcon />}
              </BaseText>
            }
            noBorder={index === permitInfoList.length - 1}
            key={`permit-info-${item.label}`}
            onPress={item?.onPress}
          />
        ) : undefined
      )}
    </>
  );
};

export default SalesOrderPermitInfo;
